import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { css } from '@emotion/css';
import ProductSelectionCard from './ProductSelectionCard';
import { styles } from './styles';
import ProductSelectionCardSkeleton from '@/components/content/welcomeModal/wohnenUpsell/ProductSelectionCardSkeleton';
import { ProductType } from '@/@types/product';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';
import { useSubscriptionArticlesConfig } from '@/customHooks/useSubscriptionArticlesConfig.hook';
import { default as Msg } from '@/i18n/components/FormattedMessage';
import { useIntl } from '@/i18n/hooks/useIntl';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { WOHNEN_UPSELL } from '@/pages/tenant/components/WohnenUpsellModal/wohnenUpsell.config';
import useWohnenPlusUpsellContentExperimentConfig from '@/customHooks/useWohnenPlusUpsellContentExperimentConfig.hook';
import { useExperimentLanguage } from '@/customHooks/useExperimentLanguage.hook';

const SubscriptionOptions = () => {
  const { t } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMeinPlus = useHasMeinPlus();
  const { upsellConfig } = useWohnenPlusUpsellContentExperimentConfig();
  const { langKey } = useExperimentLanguage();

  const { articles, isLoading } = useSubscriptionArticlesConfig(ProductType.WohnenPlus, upsellConfig);
  const articleIdFromQuery = searchParams.get('articleId');
  const [selectedArticle, setSelectedArticle] = useState(articleIdFromQuery);

  const config = useMemo(() => {
    const monthlyArticle = articles?.find((a) => a.id === String(WOHNEN_UPSELL.ARTICLE_IDS.MONTHLY));
    const yearlyArticle = articles?.find((a) => a.id === String(WOHNEN_UPSELL.ARTICLE_IDS.YEARLY));
    const monthlyPrice = (monthlyArticle?.discount as any)?.effectiveGrossPrice?.amount ?? 0;
    const yearlyPrice = (yearlyArticle?.discount as any)?.effectiveGrossPrice?.amount ?? 0;
    const benefitPercent =
      yearlyPrice && monthlyPrice ? Math.ceil(((monthlyPrice - yearlyPrice / 12) / monthlyPrice) * 100) : undefined;
    const formatPrice = (amount?: number) => (amount ? `${amount.toLocaleString('de-DE')}` : undefined);

    return {
      productType: ProductType.WohnenPlus,
      configId: upsellConfig,
      defaultArticleId: WOHNEN_UPSELL.ARTICLE_IDS.MONTHLY,
      articlesMap: {
        monthly: {
          id: WOHNEN_UPSELL.ARTICLE_IDS.MONTHLY,
          titleKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.monthly.title`,
          subtitleKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.monthly.subtitle`,
          priceTitleKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.priceTitleMonthly`,
          priceBenefitKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.monthly.priceBenefit`,
          price: {
            raw: monthlyPrice,
            formatted: formatPrice(monthlyPrice),
          },
          originalPrice: formatPrice(monthlyArticle?.grossPrice?.amount),
        },
        yearly: {
          id: WOHNEN_UPSELL.ARTICLE_IDS.YEARLY,
          titleKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.yearly.title`,
          subtitleKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.yearly.subtitle`,
          priceTitleKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.priceTitleYearly`,
          priceBenefitKey: `welcomeModal.wohnenUpsell.${langKey}.subscriptionOptions.yearly.priceBenefit`,
          price: {
            raw: yearlyPrice,
            formatted: formatPrice(yearlyPrice),
            monthlyEquivalent: yearlyPrice ? formatPrice(Math.floor((yearlyPrice / 12) * 100) / 100) : undefined,
          },
          originalPrice: formatPrice(yearlyArticle?.grossPrice?.amount),
          benefitPercent,
        },
      },
      tracking: TrackingEvents.WOHNEN_PLUS_UPSELL,
    };
  }, [articles, langKey]);

  useEffect(() => {
    if (!articleIdFromQuery) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('articleId', String(WOHNEN_UPSELL.ARTICLE_IDS.MONTHLY));
      setSearchParams(newSearchParams);
      setSelectedArticle(String(WOHNEN_UPSELL.ARTICLE_IDS.MONTHLY));
    }
  }, [articleIdFromQuery, searchParams, setSearchParams]);

  useEffect(() => {
    if (articleIdFromQuery) {
      setSelectedArticle(articleIdFromQuery);
    }
  }, [articleIdFromQuery]);

  useEffect(() => {
    report(config.tracking.PAGE_VISITED, isMeinPlus);
  }, [isMeinPlus, config.tracking.PAGE_VISITED]);

  const handleCardClick = useCallback(
    (selectedOption: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('articleId', selectedOption);
      setSearchParams(newSearchParams);
      setSelectedArticle(selectedOption);
      report(
        {
          ...config.tracking.OPTION_CLIKED,
          parameter2: `option_${WOHNEN_UPSELL.PACKAGE_TYPES[selectedOption as keyof typeof WOHNEN_UPSELL.PACKAGE_TYPES]}_${WOHNEN_UPSELL.PAYMENT_FREQUENCY[selectedOption as keyof typeof WOHNEN_UPSELL.PAYMENT_FREQUENCY]}`,
        },
        isMeinPlus,
      );
    },
    [setSearchParams, searchParams, isMeinPlus, config.tracking.OPTION_CLIKED],
  );

  return (
    <div id='selection-items' className={`${css(styles.optionsContainer)} display-flex`}>
      {isLoading
        ? Array(Object.keys(config.articlesMap).length)
            .fill(null)
            .map((_, idx) => <ProductSelectionCardSkeleton key={idx} />)
        : Object.values(config.articlesMap).map((meta: any) => {
            const articleId = String(meta.id);
            return (
              <ProductSelectionCard
                key={articleId}
                data-article-id={articleId}
                id={articleId}
                onClick={handleCardClick}
                title={t({ id: meta.titleKey })}
                subtitle={t({ id: meta.subtitleKey })}
                priceTitle={
                  <Msg
                    id={meta.priceTitleKey}
                    defaultMessage=''
                    values={{
                      monthlyPrice: meta.price.monthlyEquivalent || meta.price.formatted,
                      yearlyPrice: meta.id === WOHNEN_UPSELL.ARTICLE_IDS.YEARLY ? meta.price.formatted : undefined,
                      strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                    }}
                  />
                }
                priceBenefit={
                  <Msg
                    id={meta.priceBenefitKey}
                    defaultMessage=''
                    values={{
                      originalPrice: meta.originalPrice,
                      s: (chunks: React.ReactNode) => <s>{chunks}</s>,
                      benefitPercent: meta.benefitPercent,
                    }}
                  />
                }
                selected={selectedArticle === articleId}
              />
            );
          })}
    </div>
  );
};

export default SubscriptionOptions;
